import { useState, useEffect, useRef } from 'react';
import {
  useStripe,
  PaymentRequestButtonElement,
} from '@stripe/react-stripe-js';
import { type PaymentRequest } from '@stripe/stripe-js';
import { useHistory } from 'react-router-dom';
import { Heading } from '@hungryroot/ramen';
import { saveCard } from '../../../../frontend/react/src/api/checkout';
import { SHIPPING_PATH } from '../../../../frontend/react/src/constants/checkout';
import { PATHS } from '../../../../frontend/react/src/constants/constants';
import { getOrderTotalCost } from '../../pages/Payment/utils';
import { Disclaimer } from '../Disclaimer/Disclaimer';
import styles from './PaymentRequestButton.module.css';

interface Props {
  testid?: string;
  orderId: number;
  setErrorMessage: (error: string) => void;
  loading: boolean;
  placeOrder: () => void;
  orderTotal: string;
  subtotal: number;
  billingState: string;
  showDisclaimer: boolean;
  disabled?: boolean;
  onClick?: () => void;
  isInvalid?: boolean;
}

export function PaymentRequestButton(props: Props) {
  const {
    orderTotal,
    setErrorMessage,
    loading,
    placeOrder,
    orderId,
    subtotal,
    billingState,
    showDisclaimer,
    disabled,
    onClick,
    isInvalid,
  } = props;

  const history = useHistory();

  const stripe = useStripe();
  const [paymentRequest, setPaymentRequest] = useState<PaymentRequest | null>(
    null
  );

  // this is to capture all the variables from the function passed through props
  // i don't want to add this as a dependency to the useEffect because it will cause a re-render
  // and a re-creation of the stripe payment request
  const placeOrderRef = useRef(placeOrder);

  useEffect(() => {
    placeOrderRef.current = placeOrder;
  }, [placeOrder]);

  const orderTotalNum = getOrderTotalCost(orderTotal);

  const paymentReqOptions = {
    country: 'US',
    currency: 'usd',
    total: {
      label: 'total',
      amount: orderTotalNum,
      pending: false,
    },
  };

  useEffect(() => {
    (async () => {
      if (stripe) {
        const paymentReq = stripe.paymentRequest(paymentReqOptions);

        const result = await paymentReq.canMakePayment();
        if (result) {
          setPaymentRequest(paymentReq);

          paymentReq.on('token', async (event) => {
            if (loading) {
              return;
            }

            if ('fail' in event) {
              setErrorMessage(
                'Something went wrong. Try again or try with a traditional card.'
              );
              return;
            }

            setErrorMessage('');
            const data = {
              stripe_token: event.token.id,
              order_id: orderId,
              method_name: event.methodName,
            };
            const cardSaveResult = await saveCard(data);
            if (
              'stale_order' in cardSaveResult &&
              cardSaveResult['stale_order']
            ) {
              event.complete('fail');
              history.push(PATHS[SHIPPING_PATH]);
              return;
            }
            if ('created' in cardSaveResult && cardSaveResult['created']) {
              event.complete('success');
            }
            if (cardSaveResult['error_msg']) {
              setErrorMessage(cardSaveResult['error_msg']);
              event.complete('fail');
              return;
            }
            placeOrderRef.current();
          });
        }
      }
    })();
  }, [stripe]);

  if (paymentRequest) {
    return (
      <div className={styles['payment-request-container']}>
        {showDisclaimer && (
          <Disclaimer
            planTotal={subtotal.toString()}
            billingState={billingState}
            testid="disclaimer"
          />
        )}
        <div
          style={{
            pointerEvents: disabled ? 'none' : 'auto',
            opacity: disabled ? 0.5 : 1,
          }}
          className={styles['button-container']}
        >
          <PaymentRequestButtonElement
            options={{
              paymentRequest,
            }}
            onClick={(e) => {
              // the onclick function here is the validation side effect before the payment dialog
              // shows up we don't want to show the payment dialog if the user doesn't pass the
              // validation so we would preventDefault, where the default is the payment dialog opening
              if (isInvalid) {
                onClick?.();
                e.preventDefault();
              }
            }}
          />
        </div>
        <div className={styles['or-line-container']}>
          <div className={styles['or-line']}></div>
          <Heading variant="H6" className={styles['or-divider']}>
            OR
          </Heading>
          <div className={styles['or-line']}></div>
        </div>
      </div>
    );
  }

  return <></>;
}
